import { useState } from 'react'
import { Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TaskForm, type TaskFormState } from './_components/TaskForm'
import type { AssigneeOption, TaskDetail } from '@/types/work'

interface Props {
  task: TaskDetail
  assignee_options: AssigneeOption[]
}

// due_at comes from the server as an ISO 8601 instant in UTC (e.g.
// "2026-05-02T18:00:00Z"). <input type="datetime-local"> expects a
// "YYYY-MM-DDTHH:mm" string interpreted in the BROWSER'S local zone.
//
// Naively slicing the ISO string strips the trailing "Z" but keeps
// the UTC clock time, so the browser then re-interprets it as local —
// shifting the stored instant by the user's offset every time Edit
// is opened and saved without touching the field. Build the local
// representation explicitly via the Date constructor (which parses
// the ISO into the correct instant) and format the parts in local
// time so the wall-clock displayed matches what the user would expect.
// (Codex P1, PR #85)
function isoToLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export default function TasksEdit({ task, assignee_options }: Props) {
  const [state, setState] = useState<TaskFormState>({
    title: task.title,
    description: task.description ?? '',
    priority: task.priority,
    task_type: task.task_type,
    due_at: isoToLocalInput(task.due_at),
    assignee_user_id: task.assignee_user_id,
    subject: task.subject,
  })

  const { patch, processing, errors } = useForm({})

  function update<K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    // assignee_user_id is intentionally NOT in this payload —
    // UpdateTask::MUTABLE_KEYS excludes it, and reassignment runs
    // through the separate ReassignTask interaction with hierarchy
    // checks (TaskPolicy#reassign?). Sending it here would be silently
    // dropped server-side. (Codex P1, PR #85)
    patch(`/work/tasks/${task.id}`, {
      preserveScroll: true,
      data: {
        task: {
          title: state.title,
          description: state.description,
          priority: state.priority,
          task_type: state.task_type,
          due_at: state.due_at || null,
          subject_type: state.subject?.type ?? null,
          subject_id: state.subject?.id ?? null,
        },
      },
    } as Parameters<typeof patch>[1])
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link
          href={`/work/tasks/${task.id}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {task.title}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Modifier la tâche</h1>
      </div>

      <TaskForm
        state={state}
        onChange={update}
        onSubmit={submit}
        errors={errors as Record<string, string>}
        processing={processing}
        assigneeOptions={assignee_options}
        assigneeReadOnly
        actions={
          <>
            <Button type="button" variant="ghost" asChild>
              <Link href={`/work/tasks/${task.id}`}>Annuler</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              Enregistrer
            </Button>
          </>
        }
      />
    </div>
  )
}
